import Phaser from 'phaser';
import { GAME_WIDTH } from '../config';
import {
  EQUIPMENT_SLOT_LABELS,
  getItemDef,
  type EquipmentSlotId,
  type ItemFilterId,
} from '../../data/equipment';
import {
  displayNameForItem,
  describeEquipReject,
  equipItem,
  getEquippedInstance,
  isInstanceEquipped,
  isOffHandDisabled,
  listBagItems,
  slotOfInstance,
  unequipItem,
  type InventoryState,
  type ItemInstance,
} from '../../domain/equipment';
import {
  buildEquipmentContextFromLoadout,
  describeItemAffixes,
} from '../../domain/equipmentCombat';
import {
  deriveClassBaseStats,
} from '../../data/classBaseStats';
import {
  computeCharacterCombatStats,
} from '../../domain/characterStats';
import { canClassEquipItem } from '../../data/classEquipment';
import {
  meetsItemLevelClassRequirement,
  requiredClassLevelForItemLevel,
} from '../../data/equipmentProgression';
import { RARITY_DISPLAY_COLORS } from '../../data/equipment';
import { VANGUARD_PERMANENT_ACTIVES } from '../../data/vanguardActives';
import {
  DEV_EQUIPMENT_FIXTURE_FLAG,
  DEV_SPRITE_VERIFY_FIXTURE,
  overlayFixtureEquipment,
} from '../../persistence/equipmentFixtures';
import {
  applyInventoryToPermanent,
  createDefaultPermanentSave,
  inventoryFromPermanent,
  SAVE_SCHEMA_VERSION,
  type PermanentSave,
} from '../../persistence/schema';
import { createSaveStore } from '../../persistence/saveStore';
import { mountShell, paintShellBackdrop } from '../ui/shellUi';
import { addUiButton, addUiPanel, addUiSlot } from '../ui/kit';
import { addGameIcon, addItemIcon, EQUIP_SLOT_ICONS, type IconId } from '../ui/icons';
import { addPixelText } from '../ui/pixelText';
import { UI } from '../ui/theme';

type HeroSubTab = 'equipment' | 'bag' | 'stats' | 'skills';

/** Paper-doll slots (mockup layout). Special Die omitted — six-dice design. */
const LEFT_EQUIP_SLOTS: readonly EquipmentSlotId[] = [
  'helmet',
  'chest',
  'belt',
  'boots',
];
const RIGHT_EQUIP_SLOTS: readonly EquipmentSlotId[] = [
  'main_hand',
  'off_hand',
  'ring_1',
  'ring_2',
];

const HERO_SUB_TABS: readonly {
  id: HeroSubTab;
  label: string;
  icon: IconId;
}[] = [
  { id: 'equipment', label: 'Equipment', icon: 'equip.chest' },
  { id: 'bag', label: 'Bag', icon: 'nav.quests' },
  { id: 'stats', label: 'Stats', icon: 'stat.hp' },
  { id: 'skills', label: 'Skills', icon: 'nav.talents' },
];

type FixtureOverlayMode = 'default' | 'sprites' | null;

function fixtureOverlayMode(scene: Phaser.Scene): FixtureOverlayMode {
  const reg = scene.registry.get(DEV_EQUIPMENT_FIXTURE_FLAG);
  if (reg === DEV_SPRITE_VERIFY_FIXTURE || reg === 'sprites') return 'sprites';
  if (reg === true) return 'default';
  try {
    const params = new URLSearchParams(window.location.search);
    const v = params.get('fixtureInventory');
    if (v === 'sprites') return 'sprites';
    if (v === '1') return 'default';
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * Hero shell — Equipment / Bag / Stats / Skills.
 * Mobile-first: full-width bag grid + bottom-sheet item details; lean Equipment.
 */
export class HeroScene extends Phaser.Scene {
  private scrollRoot?: Phaser.GameObjects.Container;
  private detailOverlay?: Phaser.GameObjects.Container;
  private scrollMin = 0;
  private scrollTopBound = 0;
  private dragStartY = 0;
  private scrollStartY = 0;
  /** Restore bag scroll after closing the details sheet. */
  private savedScrollY: number | null = null;
  private permanent!: PermanentSave;
  private inventory!: InventoryState;
  private filter: ItemFilterId = 'all';
  /** Bag highlight while detail open. */
  private selectedInstanceId: string | null = null;
  /** Item currently shown in the detail panel. */
  private detailInstanceId: string | null = null;
  private statusMessage = '';
  private fixtureMode = false;
  private overlayMode: FixtureOverlayMode = null;
  private contentBottom = 0;
  private boundsHeight = 0;
  private heroTab: HeroSubTab = 'equipment';

  constructor() {
    super('Hero');
  }

  create(): void {
    paintShellBackdrop(this, 0x12101c);
    const bounds = mountShell(this, 'hero');
    this.boundsHeight = bounds.height;
    this.overlayMode = fixtureOverlayMode(this);
    this.fixtureMode = this.overlayMode != null;

    const loaded =
      createSaveStore().load()?.permanent ?? createDefaultPermanentSave();
    this.permanent = this.overlayMode
      ? overlayFixtureEquipment(loaded, this.overlayMode)
      : loaded;
    this.inventory = inventoryFromPermanent(this.permanent);

    const maskRect = this.add
      .rectangle(
        GAME_WIDTH / 2,
        bounds.top + bounds.height / 2,
        GAME_WIDTH,
        bounds.height,
        0xffffff,
        0,
      )
      .setOrigin(0.5);
    const mask = maskRect.createGeometryMask();

    this.scrollRoot = this.add.container(0, bounds.top);
    this.scrollRoot.setMask(mask);
    this.scrollRoot.setDepth(5);
    this.scrollTopBound = bounds.top;

    this.rebuildContent();

    // Scene-level scroll — do NOT lay an interactive rect over the list
    // (that steals taps from slots / bag / detail actions).
    const top = bounds.top;
    const bottom = bounds.top + bounds.height;
    let dragPointerId: number | null = null;
    let scrolling = false;
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.detailOverlay) return;
      if (pointer.y < top || pointer.y > bottom) return;
      dragPointerId = pointer.id;
      this.dragStartY = pointer.y;
      this.scrollStartY = this.scrollRoot?.y ?? top;
      scrolling = false;
    });
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (
        this.detailOverlay ||
        !pointer.isDown ||
        !this.scrollRoot ||
        dragPointerId !== pointer.id
      ) {
        return;
      }
      const dy = pointer.y - this.dragStartY;
      if (!scrolling && Math.abs(dy) < 12) return;
      scrolling = true;
      this.scrollRoot.y = Phaser.Math.Clamp(
        this.scrollStartY + dy,
        top + this.scrollMin,
        top,
      );
    });
    this.input.on('pointerup', () => {
      dragPointerId = null;
      scrolling = false;
    });

    // DEV verify hook for automated screenshots (no player-facing UI).
    if (typeof window !== 'undefined') {
      (
        window as unknown as {
          __rollbreakHero?: HeroScene;
        }
      ).__rollbreakHero = this;
    }
  }

  private persistInventory(): void {
    if (this.fixtureMode) {
      // Session overlay only — never write fabricated fixture gear to real saves.
      this.permanent = applyInventoryToPermanent(this.permanent, this.inventory);
      return;
    }
    const store = createSaveStore();
    const previous = store.load();
    const permanent = applyInventoryToPermanent(
      previous?.permanent ?? this.permanent,
      this.inventory,
    );
    this.permanent = permanent;
    store.save({
      version: previous?.version ?? SAVE_SCHEMA_VERSION,
      savedAt: Date.now(),
      rngState: previous?.rngState ?? 0,
      permanent,
      run: previous?.run ?? null,
    });
  }

  private equipCtx() {
    const classId = this.permanent.profile.activeClassId;
    return {
      specialDieSlotUnlocked: this.permanent.specialDieSlotUnlocked,
      classId,
      classLevel: this.permanent.classes[classId]?.level ?? 1,
    };
  }

  private setStatus(msg: string): void {
    this.statusMessage = msg;
  }

  private isSlotLocked(slot: EquipmentSlotId): boolean {
    if (slot === 'off_hand' && isOffHandDisabled(this.inventory)) return true;
    if (slot === 'special_die' && !this.permanent.specialDieSlotUnlocked) {
      return true;
    }
    return false;
  }

  /** Prefer empty allowed slots; otherwise first allowed (for Replace). */
  private preferredEquipSlot(item: ItemInstance): EquipmentSlotId | null {
    const def = getItemDef(item.defId);
    if (!def) return null;
    const allowed = def.allowedSlots.filter((s) => !this.isSlotLocked(s));
    if (allowed.length === 0) return null;
    const empty = allowed.find(
      (s) => getEquippedInstance(this.inventory, s) == null,
    );
    return empty ?? allowed[0]!;
  }

  private openItemDetail(instanceId: string): void {
    const item = this.inventory.items.find((i) => i.instanceId === instanceId);
    if (!item) return;
    if (this.heroTab === 'bag' && this.scrollRoot) {
      this.savedScrollY = this.scrollRoot.y;
    }
    this.detailInstanceId = instanceId;
    this.selectedInstanceId = instanceId;
    this.setStatus('');
    this.showDetailOverlay(item);
  }

  private closeItemDetail(options: { rebuild?: boolean } = {}): void {
    const rebuild = options.rebuild !== false;
    this.detailInstanceId = null;
    this.detailOverlay?.destroy(true);
    this.detailOverlay = undefined;
    const restoreY = this.savedScrollY;
    this.savedScrollY = null;
    if (!rebuild) return;
    this.selectedInstanceId = null;
    this.rebuildContent();
    if (restoreY != null && this.scrollRoot) {
      this.scrollRoot.y = Phaser.Math.Clamp(
        restoreY,
        this.scrollTopBound + this.scrollMin,
        this.scrollTopBound,
      );
    }
  }

  /** Real item attributes only (no invented stats). */
  private itemAttrRows(
    item: ItemInstance,
  ): { key: string; label: string; value: number }[] {
    const def = getItemDef(item.defId);
    const rows: { key: string; label: string; value: number }[] = [];
    if (def?.baseWeaponDamage != null && def.baseWeaponDamage > 0) {
      rows.push({
        key: 'atk',
        label: 'Attack',
        value: def.baseWeaponDamage,
      });
    }
    if (def?.baseFlatArmour != null && def.baseFlatArmour > 0) {
      rows.push({
        key: 'arm',
        label: 'Armour',
        value: def.baseFlatArmour,
      });
    }
    for (const a of describeItemAffixes(item)) {
      rows.push({
        key: a.affixId,
        label: a.combatActive ? a.name : `${a.name} (inactive)`,
        value: a.value,
      });
    }
    return rows;
  }

  private onSlotTap(slot: EquipmentSlotId): void {
    if (this.isSlotLocked(slot)) {
      this.setStatus(
        slot === 'off_hand'
          ? 'Off-hand locked while two-hand weapon is equipped'
          : 'Special Die locked — unlock via General talent',
      );
      this.rebuildContent();
      return;
    }

    const equipped = getEquippedInstance(this.inventory, slot);

    // Detail open on a bag item + tap empty/filled compatible slot → equip/replace there.
    if (this.detailInstanceId && !isInstanceEquipped(this.inventory, this.detailInstanceId)) {
      const def = getItemDef(
        this.inventory.items.find((i) => i.instanceId === this.detailInstanceId)
          ?.defId ?? '',
      );
      if (def?.allowedSlots.includes(slot)) {
        this.applyEquipToSlot(this.detailInstanceId, slot);
        return;
      }
    }

    if (equipped) {
      this.openItemDetail(equipped.instanceId);
      return;
    }

    this.setStatus('Tap an inventory item for details');
    this.rebuildContent();
  }

  private onBagItemTap(item: ItemInstance): void {
    this.openItemDetail(item.instanceId);
  }

  private rarityColor(rarity: string | undefined): number {
    return Phaser.Display.Color.HexStringToColor(
      RARITY_DISPLAY_COLORS[String(rarity ?? 'common')] ?? '#b0b8c8',
    ).color;
  }

  /** Soft rarity glow behind a slot (paint before the slot chrome). */
  private paintRarityGlow(
    x: number,
    y: number,
    size: number,
    rarity: string | undefined,
    selected: boolean,
  ): void {
    const color = this.rarityColor(rarity);
    const key = String(rarity ?? 'common');
    const baseGlow =
      key === 'legendary' || key === 'mythic'
        ? 0.32
        : key === 'epic'
          ? 0.26
          : key === 'rare'
            ? 0.2
            : key === 'uncommon'
              ? 0.16
              : 0.1;
    // Outer soft plate
    this.scrollRoot!.add(
      this.add
        .rectangle(
          x,
          y,
          size + (selected ? 12 : 8),
          size + (selected ? 12 : 8),
          color,
          selected ? baseGlow + 0.1 : baseGlow * 0.7,
        )
        .setOrigin(0.5),
    );
    // Inner brighter rim glow
    this.scrollRoot!.add(
      this.add
        .rectangle(x, y, size + 4, size + 4, color, baseGlow * 0.55)
        .setOrigin(0.5),
    );
  }

  /** Rarity stroke on top of a slot (paint after the slot chrome). */
  private paintRarityBorder(
    x: number,
    y: number,
    size: number,
    rarity: string | undefined,
    selected: boolean,
  ): void {
    const color = this.rarityColor(rarity);
    // Gold outer ring when selected (mockup selection cue)
    if (selected) {
      this.scrollRoot!.add(
        this.add
          .rectangle(x, y, size + 6, size + 6)
          .setOrigin(0.5)
          .setStrokeStyle(2, UI.gold, 0.95)
          .setFillStyle(0x000000, 0),
      );
    }
    this.scrollRoot!.add(
      this.add
        .rectangle(x, y, size + 1, size + 1)
        .setOrigin(0.5)
        .setStrokeStyle(2, color, 1)
        .setFillStyle(0x000000, 0),
    );
  }

  private applyEquipToSlot(instanceId: string, slot: EquipmentSlotId): void {
    const result = equipItem(
      this.inventory,
      instanceId,
      slot,
      this.equipCtx(),
    );
    if (!result.ok) {
      const selected = this.inventory.items.find(
        (i) => i.instanceId === instanceId,
      );
      this.setStatus(
        describeEquipReject(result.reason, {
          itemLevel: selected?.itemLevel,
          requiredClassLevel: requiredClassLevelForItemLevel(
            selected?.itemLevel,
          ),
        }),
      );
      this.closeItemDetail();
      return;
    }
    const wasReplace = getEquippedInstance(this.inventory, slot) != null;
    this.inventory = result.inventory;
    if (result.unequippedToBag.length > 0) {
      this.setStatus('Equipped — previous off-hand returned to bag');
    } else if (wasReplace) {
      this.setStatus('Replaced equipped item');
    } else {
      this.setStatus('Equipped');
    }
    this.persistInventory();
    this.closeItemDetail();
  }

  private onDetailEquip(): void {
    if (!this.detailInstanceId) return;
    if (isInstanceEquipped(this.inventory, this.detailInstanceId)) return;
    const item = this.inventory.items.find(
      (i) => i.instanceId === this.detailInstanceId,
    );
    if (!item) return;
    const slot = this.preferredEquipSlot(item);
    if (!slot) {
      this.setStatus('No valid slot for this item');
      this.closeItemDetail();
      return;
    }
    this.applyEquipToSlot(item.instanceId, slot);
  }

  private onDetailUnequip(): void {
    if (!this.detailInstanceId) return;
    const slot = slotOfInstance(this.inventory, this.detailInstanceId);
    if (!slot) return;
    const equipped = getEquippedInstance(this.inventory, slot);
    this.inventory = unequipItem(this.inventory, slot);
    this.setStatus(
      equipped
        ? `Unequipped ${displayNameForItem(equipped)}`
        : 'Unequipped',
    );
    this.persistInventory();
    this.closeItemDetail();
  }

  private showDetailOverlay(item: ItemInstance): void {
    this.detailOverlay?.destroy(true);
    const def = getItemDef(item.defId);
    const equippedSlot = slotOfInstance(this.inventory, item.instanceId);
    const isEquipped = equippedSlot != null;
    const classId = this.permanent.profile.activeClassId;
    const classLevel = this.permanent.classes[classId]?.level ?? 1;
    const classOk = def ? canClassEquipItem(classId, def).ok : true;
    const levelOk = meetsItemLevelClassRequirement(classLevel, item.itemLevel);
    const reqCl = requiredClassLevelForItemLevel(item.itemLevel);
    const rarity = String(item.rarity ?? 'common');
    const rarityHex = RARITY_DISPLAY_COLORS[rarity] ?? '#b0b8c8';
    const targetSlot = isEquipped
      ? equippedSlot
      : this.preferredEquipSlot(item);
    const occupying =
      !isEquipped && targetSlot
        ? getEquippedInstance(this.inventory, targetSlot)
        : null;
    const canEquipAction =
      !isEquipped && targetSlot != null && classOk && levelOk;
    const replaceMode = Boolean(canEquipAction && occupying);
    const isSpecialDie = def?.category === 'special_die';

    const root = this.add.container(0, 0).setDepth(2000);
    const veil = this.add
      .rectangle(0, 0, GAME_WIDTH, 900, 0x000000, 0.7)
      .setOrigin(0)
      .setInteractive();
    veil.on('pointerup', () => this.closeItemDetail());
    root.add(veil);

    // Bottom-sheet style panel (mobile-friendly, above nav)
    const panelW = GAME_WIDTH - 20;
    const panelH = 520;
    const px = GAME_WIDTH / 2;
    const top = 110;
    root.add(addUiPanel(this, px - panelW / 2, top, panelW, panelH, 'ornate'));
    const panelHit = this.add
      .rectangle(px, top + panelH / 2, panelW, panelH, 0x000000, 0.001)
      .setInteractive();
    root.add(panelHit);

    // Close control (top-right)
    const closeBtn = addUiButton(
      this,
      px + panelW / 2 - 36,
      top + 22,
      '✕',
      () => this.closeItemDetail(),
      { width: 44, height: 36, fontSize: 14 },
    );
    root.add(closeBtn);

    // Large icon + rarity glow
    root.add(
      this.add
        .circle(px, top + 70, 40, this.rarityColor(rarity), 0.25)
        .setOrigin(0.5),
    );
    root.add(
      addItemIcon(this, def, item.rarity, px, top + 70, {
        size: 64,
        instanceId: item.instanceId,
        depth: 2001,
      }),
    );

    root.add(
      addPixelText(this, px, top + 118, displayNameForItem(item), {
        font: 'ui-yellow',
        size: 15,
        originX: 0.5,
        maxWidth: panelW - 80,
        align: 'center',
      }),
    );
    root.add(
      this.add
        .text(
          px,
          top + 140,
          rarity.charAt(0).toUpperCase() + rarity.slice(1),
          {
            fontFamily: 'Karla, system-ui, sans-serif',
            fontSize: '13px',
            color: rarityHex,
            fontStyle: 'bold',
          },
        )
        .setOrigin(0.5, 0),
    );

    const typeBits: string[] = [];
    if (targetSlot) typeBits.push(EQUIPMENT_SLOT_LABELS[targetSlot]);
    else if (def?.category) typeBits.push(def.category);
    if (def?.weaponFamily) typeBits.push(def.weaponFamily);
    if (def?.handedness === 'two_hand') typeBits.push('Two-hand');
    else if (def?.handedness === 'one_hand') typeBits.push('One-hand');
    if (def?.isShield) typeBits.push('Shield');
    typeBits.push(`Lv ${item.itemLevel ?? 1}`);
    root.add(
      addPixelText(this, px, top + 160, typeBits.join(' · '), {
        font: 'ui-grey',
        size: 11,
        originX: 0.5,
        maxWidth: panelW - 32,
        align: 'center',
      }),
    );
    root.add(
      addPixelText(
        this,
        px,
        top + 176,
        isEquipped
          ? `Equipped · ${EQUIPMENT_SLOT_LABELS[equippedSlot!]}`
          : `In bag · needs CL ${reqCl}${levelOk ? '' : ' (locked)'}`,
        {
          font: 'ui-grey',
          size: 10,
          originX: 0.5,
        },
      ),
    );

    // Attributes
    const attrs = this.itemAttrRows(item);
    let ly = top + 200;
    root.add(
      addPixelText(this, 28, ly, 'ATTRIBUTES', {
        font: 'ui-yellow',
        size: 11,
      }),
    );
    ly += 18;
    if (attrs.length === 0) {
      root.add(
        addPixelText(
          this,
          28,
          ly,
          isSpecialDie
            ? 'Special die — modifiers apply in combat when equipped'
            : 'No combat attributes on this item',
          { font: 'ui-grey', size: 11, maxWidth: panelW - 40 },
        ),
      );
      ly += 20;
    } else {
      for (const row of attrs.slice(0, 6)) {
        root.add(
          addPixelText(this, 28, ly, row.label, {
            font: 'ui-grey',
            size: 12,
            maxWidth: panelW - 100,
          }),
        );
        root.add(
          addPixelText(this, GAME_WIDTH - 28, ly, `+${row.value}`, {
            font: 'ui-yellow',
            size: 12,
            originX: 1,
          }),
        );
        ly += 18;
      }
    }
    if (!classOk) {
      root.add(
        addPixelText(this, 28, ly, 'Your class cannot equip this item', {
          font: 'ui-grey',
          size: 11,
        }),
      );
      ly += 18;
    }

    // Compare vs currently equipped (bag / replace flow)
    const compareItem =
      !isEquipped && targetSlot
        ? getEquippedInstance(this.inventory, targetSlot)
        : null;
    if (compareItem && compareItem.instanceId !== item.instanceId) {
      ly += 6;
      root.add(
        addPixelText(this, 28, ly, 'COMPARE VS EQUIPPED', {
          font: 'ui-yellow',
          size: 11,
        }),
      );
      ly += 16;
      root.add(
        addPixelText(
          this,
          28,
          ly,
          displayNameForItem(compareItem),
          { font: 'ui-grey', size: 10, maxWidth: panelW - 40 },
        ),
      );
      ly += 16;
      const eqMap = new Map(
        this.itemAttrRows(compareItem).map((r) => [r.key, r]),
      );
      const selMap = new Map(attrs.map((r) => [r.key, r]));
      const keys = [...new Set([...eqMap.keys(), ...selMap.keys()])];
      for (const key of keys.slice(0, 5)) {
        const eqV = eqMap.get(key)?.value ?? 0;
        const selV = selMap.get(key)?.value ?? 0;
        const label = selMap.get(key)?.label ?? eqMap.get(key)?.label ?? key;
        const delta = selV - eqV;
        const deltaColor =
          delta > 0 ? '#5ecf7a' : delta < 0 ? '#ff6b6b' : '#9aa3b5';
        const deltaText =
          delta > 0 ? `+${delta}` : delta < 0 ? `${delta}` : '=';
        root.add(
          addPixelText(this, 28, ly, `${label}  ${selV}`, {
            font: 'ui-grey',
            size: 11,
            maxWidth: panelW - 90,
          }),
        );
        root.add(
          this.add
            .text(GAME_WIDTH - 28, ly, deltaText, {
              fontFamily: 'Karla, system-ui, sans-serif',
              fontSize: '12px',
              color: deltaColor,
              fontStyle: 'bold',
            })
            .setOrigin(1, 0),
        );
        ly += 17;
      }
    }

    // Primary action pinned near bottom of sheet
    const btnY = top + panelH - 56;
    if (isEquipped) {
      root.add(
        addUiButton(this, px, btnY, 'Unequip', () => this.onDetailUnequip(), {
          width: panelW - 40,
          height: 44,
          fontSize: 14,
        }),
      );
    } else if (canEquipAction) {
      const label = replaceMode
        ? `Replace ${EQUIPMENT_SLOT_LABELS[targetSlot!]}`
        : `Equip to ${EQUIPMENT_SLOT_LABELS[targetSlot!]}`;
      root.add(
        addUiButton(this, px, btnY, label, () => this.onDetailEquip(), {
          width: panelW - 40,
          height: 44,
          fontSize: 13,
        }),
      );
      if (replaceMode && occupying) {
        root.add(
          addPixelText(
            this,
            px,
            btnY - 22,
            `Replaces ${displayNameForItem(occupying)}`,
            { font: 'ui-grey', size: 10, originX: 0.5 },
          ),
        );
      }
    } else {
      root.add(
        addUiButton(
          this,
          px,
          btnY,
          !classOk
            ? 'Class locked'
            : !levelOk
              ? `Needs class Lv ${reqCl}`
              : 'Cannot equip',
          () => undefined,
          {
            width: panelW - 40,
            height: 44,
            fontSize: 12,
            state: 'disabled',
          },
        ),
      );
    }

    this.detailOverlay = root;
  }

  private showDiceDetail(slotIndex: number): void {
    this.detailOverlay?.destroy(true);
    const unlocked = slotIndex < this.permanent.unlockedDiceSlots;
    const root = this.add.container(0, 0).setDepth(2000);
    const veil = this.add
      .rectangle(0, 0, GAME_WIDTH, 900, 0x000000, 0.7)
      .setOrigin(0)
      .setInteractive();
    veil.on('pointerup', () => {
      this.detailOverlay?.destroy(true);
      this.detailOverlay = undefined;
    });
    root.add(veil);

    const panelW = GAME_WIDTH - 32;
    const panelH = 220;
    const px = GAME_WIDTH / 2;
    const top = 220;
    root.add(addUiPanel(this, px - panelW / 2, top, panelW, panelH, 'ornate'));
    root.add(
      this.add
        .rectangle(px, top + panelH / 2, panelW, panelH, 0x000000, 0.001)
        .setInteractive(),
    );
    root.add(
      addGameIcon(this, 'stat.dice', px, top + 48, {
        size: 40,
        color: unlocked ? UI.gold : UI.ironLight,
        alpha: unlocked ? 1 : 0.45,
      }),
    );
    root.add(
      addPixelText(this, px, top + 84, `Die ${slotIndex + 1}`, {
        font: 'ui-yellow',
        size: 16,
        originX: 0.5,
      }),
    );
    root.add(
      addPixelText(
        this,
        px,
        top + 112,
        unlocked
          ? 'Unlocked — skills are assigned to dice during dungeon runs.'
          : 'Locked — unlock more dice slots via the General talent tree.',
        {
          font: 'ui-grey',
          size: 12,
          originX: 0.5,
          maxWidth: panelW - 36,
          align: 'center',
        },
      ),
    );
    root.add(
      addUiButton(
        this,
        px,
        top + panelH - 40,
        'Close',
        () => {
          this.detailOverlay?.destroy(true);
          this.detailOverlay = undefined;
        },
        { width: 140, height: 40, fontSize: 12 },
      ),
    );
    this.detailOverlay = root;
  }

  private combatSnapshot() {
    const cls = this.permanent.classes[this.permanent.profile.activeClassId];
    const classRanks = cls.talentRanks ?? {};
    const generalRanks = this.permanent.general.talentRanks ?? {};
    const classLevel = cls.level ?? 1;
    const classId = this.permanent.profile.activeClassId ?? 'vanguard';
    const equipmentCtx = buildEquipmentContextFromLoadout(
      this.inventory,
      classRanks,
    );
    const effective = computeCharacterCombatStats(
      classRanks,
      equipmentCtx,
      generalRanks,
      { classId, classLevel },
    );
    const talentOnly = computeCharacterCombatStats(
      classRanks,
      {},
      generalRanks,
      { classId, classLevel },
    );
    const classOnly = computeCharacterCombatStats({}, {}, {}, {
      classId,
      classLevel,
    });
    const classDerived = deriveClassBaseStats(classId, classLevel);
    const stanceAtk =
      effective.attackDamageBonusPct -
      talentOnly.attackDamageBonusPct -
      (equipmentCtx.attackDamageBonusPct ?? 0);
    return {
      cls,
      classId,
      classLevel,
      className: classId.toUpperCase(),
      equipmentCtx,
      effective,
      talentOnly,
      classOnly,
      classDerived,
      stanceAtk,
    };
  }

  private paintSubTabs(y: number): number {
    const cx = GAME_WIDTH / 2;
    const tabW = 84;
    const gap = 6;
    const total = HERO_SUB_TABS.length * tabW + (HERO_SUB_TABS.length - 1) * gap;
    const x0 = cx - total / 2 + tabW / 2;
    HERO_SUB_TABS.forEach((tab, i) => {
      const active = this.heroTab === tab.id;
      const bx = x0 + i * (tabW + gap);
      const btn = addUiButton(
        this,
        bx,
        y,
        tab.label,
        () => {
          this.heroTab = tab.id;
          this.closeItemDetail();
          if (this.scrollRoot) this.scrollRoot.y = this.scrollTopBound;
          this.rebuildContent();
        },
        {
          width: tabW,
          height: 34,
          state: active ? 'selected' : 'normal',
          fontSize: 9,
        },
      );
      this.scrollRoot!.add(btn);
    });
    return y + 44;
  }

  private paintEquipSlot(
    slot: EquipmentSlotId,
    sx: number,
    sy: number,
    slotSize: number,
  ): void {
    const item = getEquippedInstance(this.inventory, slot);
    const locked =
      (slot === 'off_hand' && isOffHandDisabled(this.inventory)) ||
      (slot === 'special_die' && !this.permanent.specialDieSlotUnlocked);
    if (item && !locked) {
      this.paintRarityGlow(sx, sy, slotSize, String(item.rarity), false);
    }
    const slotNode = addUiSlot(
      this,
      sx,
      sy,
      slotSize,
      locked ? 'disabled' : item ? 'normal' : 'empty',
    );
    slotNode.setSize(slotSize, slotSize);
    slotNode.setInteractive({ useHandCursor: true });
    slotNode.on('pointerup', () => this.onSlotTap(slot));
    this.scrollRoot!.add(slotNode);
    if (item && !locked) {
      this.paintRarityBorder(sx, sy, slotSize, String(item.rarity), false);
    }
    if (locked) {
      slotNode.setAlpha(0.45);
    }

    if (item) {
      const def = getItemDef(item.defId);
      this.scrollRoot!.add(
        addItemIcon(this, def, item.rarity, sx, sy - 4, {
          size: 28,
          alpha: locked ? 0.35 : 1,
          instanceId: item.instanceId,
        }),
      );
    } else {
      this.scrollRoot!.add(
        addGameIcon(
          this,
          locked ? 'ui.locked' : (EQUIP_SLOT_ICONS[slot] ?? 'equip.helmet'),
          sx,
          sy - 2,
          {
            size: 24,
            color: UI.ironLight,
            alpha: locked ? 0.4 : 0.85,
          },
        ),
      );
    }

    const label =
      slot === 'off_hand' && locked
        ? 'Off-Hand (2H)'
        : slot === 'ring_1'
          ? 'Ring 1'
          : slot === 'ring_2'
            ? 'Ring 2'
            : EQUIPMENT_SLOT_LABELS[slot];
    this.scrollRoot!.add(
      addPixelText(this, sx, sy + slotSize / 2 + 2, label, {
        font: 'ui-yellow',
        size: 9,
        originX: 0.5,
      }),
    );
  }

  private paintDiceSlots(y: number): number {
    const cx = GAME_WIDTH / 2;
    this.scrollRoot!.add(
      addPixelText(this, cx, y, 'DICE SLOTS', {
        font: 'ui-yellow',
        size: 13,
        originX: 0.5,
      }),
    );
    y += 20;
    const unlocked = this.permanent.unlockedDiceSlots;
    const size = 48;
    const gap = 8;
    const total = 6 * size + 5 * gap;
    const x0 = cx - total / 2 + size / 2;
    for (let i = 0; i < 6; i += 1) {
      const sx = x0 + i * (size + gap);
      const open = i < unlocked;
      const node = addUiSlot(
        this,
        sx,
        y + size / 2,
        size,
        open ? 'selected' : 'empty',
      );
      node.setSize(size, size);
      node.setInteractive({ useHandCursor: true });
      node.on('pointerup', () => this.showDiceDetail(i));
      this.scrollRoot!.add(node);
      this.scrollRoot!.add(
        addGameIcon(this, open ? 'stat.dice' : 'ui.locked', sx, y + size / 2 - 2, {
          size: 22,
          color: open ? UI.gold : UI.ironLight,
          alpha: open ? 1 : 0.4,
        }),
      );
      this.scrollRoot!.add(
        addPixelText(this, sx, y + size + 6, String(i + 1), {
          font: open ? 'ui-yellow' : 'ui-grey',
          size: 10,
          originX: 0.5,
        }),
      );
    }
    return y + size + 30;
  }

  /** Full Stats tab — grouped combat attributes with contributions. No Speed. */
  private paintStatsPanel(y: number): number {
    const snap = this.combatSnapshot();
    const cx = GAME_WIDTH / 2;
    const panelW = GAME_WIDTH - 28;

    this.scrollRoot!.add(
      addPixelText(
        this,
        cx,
        y,
        `${snap.className} · CLASS LV ${snap.classLevel}`,
        { font: 'ui-yellow', size: 14, originX: 0.5 },
      ),
    );
    y += 22;

    type StatRow = {
      label: string;
      value: string;
      detail: string;
      icon: IconId;
      color: number;
    };
    const groups: { title: string; rows: StatRow[] }[] = [
      {
        title: 'SURVIVAL',
        rows: [
          {
            label: 'Max HP',
            value: `${snap.effective.maxHp}`,
            detail: `base ${snap.classDerived.breakdown.baseMaxHp} · lv ${snap.classDerived.breakdown.levelMaxHp} · tal ${Math.max(0, snap.talentOnly.maxHp - snap.classOnly.maxHp)} · eq ${snap.equipmentCtx.flatMaxHp ?? 0}`,
            icon: 'stat.hp',
            color: UI.ruby,
          },
          {
            label: 'Armour',
            value: `${Math.floor(snap.effective.armourRating)}`,
            detail: `base ${snap.classDerived.breakdown.baseFlatArmour} · lv ${snap.classDerived.breakdown.levelFlatArmour} · eq ${Math.round(snap.effective.equipmentFlatArmour * 100) / 100}`,
            icon: 'stat.shield',
            color: UI.cobalt,
          },
          {
            label: 'Block / Dodge',
            value: `${snap.effective.blockChancePp}pp / ${snap.effective.dodgeChancePp}pp`,
            detail: 'From talents and equipment affixes',
            icon: 'stat.shield',
            color: UI.cyan,
          },
        ],
      },
      {
        title: 'OFFENCE',
        rows: [
          {
            label: 'Attack (flat)',
            value: `${snap.effective.flatAttack}`,
            detail: `base ${snap.classDerived.breakdown.baseFlatAttack} · lv ${snap.classDerived.breakdown.levelFlatAttack}`,
            icon: 'equip.main_hand',
            color: UI.gold,
          },
          {
            label: 'Attack %',
            value: `+${snap.effective.attackDamageBonusPct}%`,
            detail: `tal ${snap.talentOnly.attackDamageBonusPct} · eq ${snap.equipmentCtx.attackDamageBonusPct ?? 0}${snap.stanceAtk ? ` · stance ${snap.stanceAtk}` : ''}`,
            icon: 'stat.dice',
            color: UI.gold,
          },
          {
            label: 'Weapon swing',
            value:
              snap.effective.weaponSwingDamage > 0
                ? `${snap.effective.weaponSwingDamage}`
                : '0 (unarmed)',
            detail:
              snap.effective.weaponSwingDamage > 0
                ? `MH ${snap.effective.weaponSwingMainHand}${
                    snap.equipmentCtx.dualWielding
                      ? ` + OH ${snap.effective.weaponSwingOffHand}`
                      : ''
                  }`
                : 'Equip a weapon to gain swing damage',
            icon: 'equip.main_hand',
            color: UI.parchment,
          },
          {
            label: 'Crit',
            value: `${snap.effective.critChancePp}pp / +${snap.effective.critDamageBonusPct}%`,
            detail: 'Chance (pp) and crit damage bonus',
            icon: 'stat.dice',
            color: UI.gold,
          },
        ],
      },
      {
        title: 'PROGRESSION',
        rows: [
          {
            label: 'Dice slots',
            value: `${this.permanent.unlockedDiceSlots} / 6`,
            detail: 'Unlock more via General talents',
            icon: 'stat.dice',
            color: UI.cyan,
          },
        ],
      },
    ];

    for (const group of groups) {
      this.scrollRoot!.add(
        addPixelText(this, 20, y, group.title, {
          font: 'ui-yellow',
          size: 12,
        }),
      );
      y += 16;
      const rowH = 44;
      const panelH = 10 + group.rows.length * rowH;
      this.scrollRoot!.add(
        addUiPanel(this, cx - panelW / 2, y, panelW, panelH, 'stone'),
      );
      group.rows.forEach((row, i) => {
        const ry = y + 10 + i * rowH;
        this.scrollRoot!.add(
          addGameIcon(this, row.icon, 34, ry + 14, {
            size: 18,
            color: row.color,
          }),
        );
        this.scrollRoot!.add(
          addPixelText(this, 52, ry + 2, row.label, {
            font: 'ui-grey',
            size: 11,
          }),
        );
        this.scrollRoot!.add(
          addPixelText(this, GAME_WIDTH - 24, ry + 2, row.value, {
            font: 'ui-yellow',
            size: 13,
            originX: 1,
          }),
        );
        this.scrollRoot!.add(
          addPixelText(this, 52, ry + 20, row.detail, {
            font: 'ui-grey',
            size: 10,
            maxWidth: panelW - 70,
          }),
        );
      });
      y += panelH + 14;
    }
    return y;
  }

  private rebuildContent(): void {
    if (!this.scrollRoot) return;
    this.scrollRoot.removeAll(true);

    let y = 10;
    const cx = GAME_WIDTH / 2;
    const snap = this.combatSnapshot();

    if (this.fixtureMode) {
      this.scrollRoot.add(
        addPixelText(
          this,
          cx,
          y,
          this.overlayMode === 'sprites'
            ? 'DEV sprite bands — not saved'
            : 'DEV fixture overlay — not saved',
          { font: 'ui-grey', size: 9, originX: 0.5 },
        ),
      );
      y += 16;
    }

    y = this.paintSubTabs(y);

    if (this.statusMessage) {
      this.scrollRoot.add(
        addPixelText(this, cx, y, this.statusMessage, {
          font: 'ui-yellow',
          size: 10,
          originX: 0.5,
          maxWidth: GAME_WIDTH - 32,
          align: 'center',
        }),
      );
      y += 18;
    }

    if (this.heroTab === 'equipment') {
      y = this.paintEquipmentTab(y, snap.className, snap.classLevel);
    } else if (this.heroTab === 'bag') {
      y = this.paintBagTab(y);
    } else if (this.heroTab === 'stats') {
      y = this.paintStatsPanel(y);
    } else {
      y = this.paintSkillsTab(y);
    }

    y += 16;
    this.contentBottom = y;
    this.scrollMin = Math.min(0, this.boundsHeight - this.contentBottom);
    if (this.scrollRoot) {
      this.scrollRoot.y = Phaser.Math.Clamp(
        this.scrollRoot.y,
        this.scrollTopBound + this.scrollMin,
        this.scrollTopBound,
      );
    }
  }

  private paintEquipmentTab(
    y: number,
    className: string,
    classLevel: number,
  ): number {
    const cx = GAME_WIDTH / 2;
    const slotSize = 58;
    const gapY = 20;
    const portraitW = 148;
    const portraitH = 200;
    const colLeft = 52;
    const colRight = GAME_WIDTH - 52;
    const blockH = 4 * (slotSize + gapY) - gapY;
    const portraitTop = y + Math.max(0, (blockH - portraitH) / 2);

    LEFT_EQUIP_SLOTS.forEach((slot, i) => {
      this.paintEquipSlot(
        slot,
        colLeft,
        y + i * (slotSize + gapY) + slotSize / 2,
        slotSize,
      );
    });
    RIGHT_EQUIP_SLOTS.forEach((slot, i) => {
      this.paintEquipSlot(
        slot,
        colRight,
        y + i * (slotSize + gapY) + slotSize / 2,
        slotSize,
      );
    });

    this.scrollRoot!.add(
      addUiPanel(
        this,
        cx - portraitW / 2,
        portraitTop,
        portraitW,
        portraitH,
        'ornate',
      ),
    );
    if (this.textures.exists('hero-vanguard-preview')) {
      const img = this.add
        .image(cx, portraitTop + portraitH / 2 - 10, 'hero-vanguard-preview')
        .setDisplaySize(120, 136)
        .setOrigin(0.5);
      this.scrollRoot!.add(img);
    } else {
      this.scrollRoot!.add(
        addGameIcon(this, 'hud.portrait', cx, portraitTop + portraitH / 2 - 10, {
          size: 64,
          color: UI.parchmentDim,
        }),
      );
    }
    this.scrollRoot!.add(
      addPixelText(this, cx, portraitTop + portraitH - 28, className, {
        font: 'ui-yellow',
        size: 12,
        originX: 0.5,
      }),
    );
    this.scrollRoot!.add(
      addPixelText(
        this,
        cx,
        portraitTop + portraitH - 12,
        `Class Lv ${classLevel}`,
        { font: 'ui-grey', size: 10, originX: 0.5 },
      ),
    );

    y += blockH + 14;
    this.scrollRoot!.add(
      addPixelText(this, cx, y, 'Tap gear or dice for details', {
        font: 'ui-grey',
        size: 10,
        originX: 0.5,
      }),
    );
    y += 20;
    // Detailed attributes live on Stats — keep Equipment lean.
    y = this.paintDiceSlots(y);
    return y;
  }

  /** Full-width 4-col bag grid; details open as overlay (not a side column). */
  private paintBagTab(y: number): number {
    const cx = GAME_WIDTH / 2;
    const pad = 16;
    const totalItems = this.inventory.items.length;
    // Unlimited storage (domain rule) — show count as capacity readout.
    this.scrollRoot!.add(
      addPixelText(this, pad, y, 'BAG', {
        font: 'ui-yellow',
        size: 14,
      }),
    );
    this.scrollRoot!.add(
      addPixelText(
        this,
        GAME_WIDTH - pad,
        y,
        `${totalItems} items · unlimited`,
        {
          font: 'ui-grey',
          size: 11,
          originX: 1,
        },
      ),
    );
    y += 22;

    const iconFilters: { id: ItemFilterId; icon: IconId; label: string }[] = [
      { id: 'all', icon: 'action.filter', label: 'All' },
      { id: 'weapons', icon: 'equip.main_hand', label: 'Weapons' },
      { id: 'armour', icon: 'equip.chest', label: 'Armour' },
      { id: 'accessories', icon: 'equip.ring', label: 'Rings' },
      { id: 'special_dice', icon: 'equip.special_die', label: 'Dice' },
    ];
    const fSize = 44;
    const fGap = 10;
    const fTotal = iconFilters.length * fSize + (iconFilters.length - 1) * fGap;
    const f0 = cx - fTotal / 2 + fSize / 2;
    iconFilters.forEach((f, i) => {
      const bx = f0 + i * (fSize + fGap);
      const active = this.filter === f.id;
      const chip = this.add
        .rectangle(bx, y + fSize / 2, fSize, fSize, 0x12182a, 1)
        .setStrokeStyle(active ? 2 : 1, active ? UI.gold : 0x2a3348, 1)
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true });
      chip.on('pointerup', () => {
        this.filter = f.id;
        this.rebuildContent();
      });
      this.scrollRoot!.add(chip);
      this.scrollRoot!.add(
        addGameIcon(this, f.icon, bx, y + fSize / 2 - 4, {
          size: 20,
          color: active ? UI.gold : UI.ironLight,
        }),
      );
      this.scrollRoot!.add(
        addPixelText(this, bx, y + fSize + 2, f.label, {
          font: active ? 'ui-yellow' : 'ui-grey',
          size: 8,
          originX: 0.5,
        }),
      );
    });
    y += fSize + 18;

    const bag = listBagItems(this.inventory, this.filter);
    const invCols = 4;
    const invGap = 12;
    const invSize = Math.floor(
      (GAME_WIDTH - pad * 2 - (invCols - 1) * invGap) / invCols,
    );
    const invGridW = invCols * invSize + (invCols - 1) * invGap;
    const invStartX = cx - invGridW / 2;
    const minRows = 4;
    const invSlots = Math.max(
      minRows * invCols,
      Math.ceil(Math.max(bag.length, 1) / invCols) * invCols,
    );

    for (let i = 0; i < invSlots; i += 1) {
      const col = i % invCols;
      const row = Math.floor(i / invCols);
      const ix = invStartX + col * (invSize + invGap) + invSize / 2;
      const iy = y + row * (invSize + invGap) + invSize / 2;
      const item = bag[i];
      const selected = Boolean(
        item && item.instanceId === this.selectedInstanceId,
      );

      if (item) {
        this.paintRarityGlow(ix, iy, invSize, String(item.rarity), selected);
      }

      const cell = this.add
        .rectangle(ix, iy, invSize, invSize, 0x12182a, 1)
        .setStrokeStyle(1, item ? 0x2a3348 : 0x1a2236, item ? 0.95 : 0.65)
        .setOrigin(0.5);
      this.scrollRoot!.add(cell);

      if (item) {
        cell.setInteractive({ useHandCursor: true });
        cell.on('pointerup', () => this.onBagItemTap(item));
        this.paintRarityBorder(ix, iy, invSize, String(item.rarity), selected);
        const def = getItemDef(item.defId);
        const classOk = def
          ? canClassEquipItem(this.permanent.profile.activeClassId, def).ok
          : true;
        const classLevel =
          this.permanent.classes[this.permanent.profile.activeClassId]
            ?.level ?? 1;
        const levelOk = meetsItemLevelClassRequirement(
          classLevel,
          item.itemLevel,
        );
        this.scrollRoot!.add(
          addItemIcon(this, def, item.rarity, ix, iy, {
            size: Math.floor(invSize * 0.58),
            alpha: classOk && levelOk ? 1 : 0.4,
            instanceId: item.instanceId,
          }),
        );
      }
    }

    const invRows = Math.ceil(invSlots / invCols);
    y += invRows * (invSize + invGap) + 8;

    if (bag.length === 0) {
      this.scrollRoot!.add(
        addPixelText(
          this,
          cx,
          y,
          totalItems === 0
            ? 'No banked equipment yet'
            : 'No items in this filter',
          { font: 'ui-grey', size: 12, originX: 0.5 },
        ),
      );
      y += 28;
    } else {
      this.scrollRoot!.add(
        addPixelText(this, cx, y, 'Tap an item for details', {
          font: 'ui-grey',
          size: 10,
          originX: 0.5,
        }),
      );
      y += 22;
    }
    return y;
  }

  private paintSkillsTab(y: number): number {
    const cx = GAME_WIDTH / 2;
    const snap = this.combatSnapshot();
    const unlocked = new Set(snap.effective.unlockedActiveSkillIds ?? []);

    this.scrollRoot!.add(
      addPixelText(this, cx, y, 'ACTIVE SKILLS', {
        font: 'ui-yellow',
        size: 14,
        originX: 0.5,
      }),
    );
    y += 18;
    this.scrollRoot!.add(
      addPixelText(
        this,
        cx,
        y,
        'Unlocked on Talents · assigned to dice in dungeons',
        {
          font: 'ui-grey',
          size: 10,
          originX: 0.5,
          maxWidth: GAME_WIDTH - 32,
          align: 'center',
        },
      ),
    );
    y += 22;

    const panelW = GAME_WIDTH - 28;
    for (const skill of VANGUARD_PERMANENT_ACTIVES) {
      const isOpen = unlocked.has(skill.id);
      const cardH = isOpen ? 96 : 72;
      this.scrollRoot!.add(
        addUiPanel(this, cx - panelW / 2, y, panelW, cardH, 'stone'),
      );
      this.scrollRoot!.add(
        addGameIcon(
          this,
          isOpen ? 'nav.talents' : 'ui.locked',
          36,
          y + 22,
          {
            size: 22,
            color: isOpen ? UI.gold : UI.ironLight,
            alpha: isOpen ? 1 : 0.45,
          },
        ),
      );
      this.scrollRoot!.add(
        addPixelText(this, 56, y + 10, skill.name, {
          font: isOpen ? 'ui-yellow' : 'ui-grey',
          size: 13,
          maxWidth: panelW - 120,
        }),
      );
      this.scrollRoot!.add(
        addPixelText(
          this,
          GAME_WIDTH - 24,
          y + 12,
          isOpen ? `Lv 1–${skill.maxLevel}` : 'Locked',
          {
            font: 'ui-grey',
            size: 11,
            originX: 1,
          },
        ),
      );
      this.scrollRoot!.add(
        addPixelText(
          this,
          56,
          y + 32,
          isOpen
            ? skill.description
            : 'Unlock this permanent active on the Vanguard talent tree.',
          {
            font: 'ui-grey',
            size: 10,
            maxWidth: panelW - 72,
          },
        ),
      );
      if (isOpen && skill.equipmentGate?.requireOffHandWeapon) {
        this.scrollRoot!.add(
          addPixelText(this, 56, y + cardH - 18, 'Requires off-hand weapon for dungeon offers', {
            font: 'ui-grey',
            size: 9,
            maxWidth: panelW - 72,
          }),
        );
      }
      y += cardH + 10;
    }
    return y + 8;
  }
}
